import { useMemo } from "react"
import { type Query as CubeQuery } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useDate from "~/ui-rtk/hooks/date"
import { DEFAULT_DAILY_BUDGET_EVAL_PERIOD } from "~/ui-rtk/constants/daily-budget-eval"
import {
  DAILY_BUDGET_LOOKBACK_DAYS,
  dailyBudgetFromMetrics,
  dailyBudgetMetricsQuery,
} from "~/shared/budget-recommender/daily-budget"

const dayjs = useDate()

const LOOKBACK_DAYS = DAILY_BUDGET_LOOKBACK_DAYS

/**
 * Days the average first order value is measured over: every complete day
 * with data in the fetched window. Exported so UI copy can state the window.
 */
export const FIRST_ORDER_VALUE_LOOKBACK_DAYS = LOOKBACK_DAYS

interface UseRecommendedDailyBudgetParams {
  targetMerType: "amer" | "mer"
  targetMerValue: number
  targetMerScope: "ecomm" | "total"
  /** Number of complete days to average over (1, 3, or 7; defaults to 7). */
  evalPeriodDays?: number
}

interface UseRecommendedDailyBudgetResult {
  recommendedDailyBudget: number | null
  fallbackDailySpend: number | null
  /**
   * Average first order value (new-customer revenue / new-customer orders)
   * for `targetMerScope` over every complete day in the fetched window. This
   * is what converts a CAC guardrail to and from its stored aMER
   * (CAC = firstOrderValue / aMER). null until data arrives or when the
   * window has no new-customer orders.
   */
  firstOrderValue: number | null
  isLoading: boolean
  hasData: boolean
}

/**
 * Single-query approach: fetch the last LOOKBACK_DAYS of daily revenue data,
 * then pick the most recent `evalPeriodDays` complete days for averaging.
 *
 * This avoids the fragile two-query chain (find-max-date → fetch-window) that
 * interacts poorly with the capabilities-check skip logic in useCubeQuery.
 */
export function useRecommendedDailyBudget({
  targetMerType,
  targetMerValue,
  targetMerScope,
  evalPeriodDays = DEFAULT_DAILY_BUDGET_EVAL_PERIOD,
}: UseRecommendedDailyBudgetParams): UseRecommendedDailyBudgetResult {
  const query: CubeQuery = useMemo(() => {
    // Static date range — matches the pattern used by useRecommendedRoas
    const endDate = dayjs().format("YYYY-MM-DD")
    const startDate = dayjs()
      .subtract(LOOKBACK_DAYS, "day")
      .format("YYYY-MM-DD")

    return dailyBudgetMetricsQuery(startDate, endDate)
  }, [])

  const { resultSet, isLoading, error } = useCubeQuery(query)

  const result = useMemo((): UseRecommendedDailyBudgetResult => {
    if (error) {
      return {
        recommendedDailyBudget: null,
        fallbackDailySpend: null,
        firstOrderValue: null,
        isLoading: false,
        hasData: false,
      }
    }

    if (!resultSet) {
      return {
        recommendedDailyBudget: null,
        fallbackDailySpend: null,
        firstOrderValue: null,
        isLoading,
        hasData: false,
      }
    }

    // The math is shared with the API's budget_recommend tool.
    return {
      ...dailyBudgetFromMetrics(resultSet.tablePivot(), {
        targetMerType,
        targetMerValue,
        targetMerScope,
        evalPeriodDays,
      }),
      isLoading: false,
    }
  }, [
    resultSet,
    error,
    isLoading,
    targetMerType,
    targetMerValue,
    targetMerScope,
    evalPeriodDays,
  ])

  return result
}
